// apiService.js
import appVar from './appVar';
import logger from '../Utils/logger';

// Autenticación por token de sesión (2026-10-16): ValidaLogin lo entrega y viaja
// como "Authorization: Bearer <token>". Antes del login no hay token y no se manda
// el encabezado (solo responden los endpoints abiertos: regionales, ValidaUsuario,
// ValidaLogin, OlvidoPassword). Reemplaza la clave Basic fija del config.json.
const authHeaders = () => (appVar.gTokenApi ? { 'Authorization': `Bearer ${appVar.gTokenApi}` } : {});

let sesionVencidaAvisada = false;

// 401 = la sesión venció (12 h), el token no es válido o no hay token (p.ej. la
// pestaña quedó abierta durante el cambio de versión): se cierra y se vuelve al
// login. Las llamadas previas al login son abiertas y nunca dan 401. Una sola
// vez aunque fallen varias llamadas en paralelo.
const cerrarSesionVencida = () => {
    if (sesionVencidaAvisada) return;
    sesionVencidaAvisada = true;
    appVar.gTokenApi = "";
    alert("Su sesión venció. Vuelva a iniciar sesión.");
    window.location.assign(`${process.env.PUBLIC_URL}/login`);
};

const apiFetch = async (url, options = {}) => {
    const response = await fetch(url, {
        ...options,
        headers: { ...authHeaders(), ...(options.headers || {}) }
    });
    if (response.status === 401 && !window.location.pathname.endsWith('/login')) {
        cerrarSesionVencida();
    }
    return response;
};

export const fetchGetData = async (endpoint, params) => {
    try {

        //alert(endpoint);
        //alert(params);
        // Validar que el endpoint y los parámetros no sean nulos o indefinidos
        if (!endpoint || !params) {
            throw new Error("Endpoint o parámetros no válidos.");
        }


        // Escapar los parámetros
        const encodedParams = params.split('/').map(encodeURIComponent).join('/');

        // Combinar la URL con los parámetros
        const url = `${appVar.gApiUrl}/${endpoint}/${encodedParams}`;

        const response = await apiFetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Verificar si la respuesta no es OK (manejo de errores HTTP)
        if (!response.ok) {
            const errorMessage = await response.text();  // Intentar leer el mensaje de error si existe
            throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorMessage}`);
        }

        // Parsear la respuesta como JSON
        const data = await response.json();
        return data;

    } catch (error) {
        logger.error(`API GET ${endpoint} falló`, { endpoint, params, detalle: error.message || String(error) });
        throw error;
    }
};

export const fetchPostData = async (endpoint, params) => {
    try {
        // Validar que el endpoint y los parámetros no sean nulos o indefinidos
        if (!endpoint || !params) {
            throw new Error("Endpoint o parámetros no válidos.");
        }


        // Determinar si es FormData o datos JSON
        const isFormData = params instanceof FormData;
        
        const headers = {};

        let body;
        
        if (isFormData) {
            // Para FormData, NO establecer Content-Type manualmente (el navegador lo hace automáticamente)
            body = params;
        } else {
            // Para datos JSON
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(params);
        }

        // Realizar la solicitud POST con los parámetros en el cuerpo
        const response = await apiFetch(`${appVar.gApiUrl}/${endpoint}`, {
            method: 'POST',
            headers: headers,
            body: body
        });

        // Verificar si la respuesta no es OK (manejo de errores HTTP)
        if (!response.ok) {
            const errorText = await response.text();  // Intentar leer el mensaje de error si existe
            let errorPayload = null;

            try {
                errorPayload = JSON.parse(errorText);
            } catch {
                // Si no es JSON, conservamos el texto plano
            }

            const errorMessage = errorPayload?.mensaje || errorPayload?.message || errorText || `HTTP error ${response.status}`;
            const error = new Error(`HTTP error! Status: ${response.status}, Message: ${errorMessage}`);
            if (errorPayload) {
                error.response = errorPayload;
            }
            throw error;
        }

        // Parsear la respuesta como JSON o texto si no es JSON
        const text = await response.text();
        try {
            return JSON.parse(text);
        } catch {
            return text;
        }

    } catch (error) {
        // Mostrar el error en la consola y relanzarlo para que pueda ser manejado por el código que llama esta función
        logger.error(`API POST ${endpoint} falló`, { endpoint, status: error.response?.codigo, detalle: error.message || String(error) });
        throw error;
    }
};



export const fetchGetReport_okPDF = async (endpoint, params) => {
    try {

        // alert(endpoint);
        // alert(params);

        if (!endpoint || !params) {
            throw new Error("Endpoint o parámetros no válidos.");
        }


        // Escapar los parámetros
        const encodedParams = params.split('/').map(encodeURIComponent).join('/');

        // Combinar la URL con los parámetros
        const url = `${appVar.gApiUrl}/${endpoint}/${encodedParams}`;

        const response = await apiFetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/pdf'
                //'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorData = await response.text();
            throw new Error(`Error ${response.status}: ${errorData}`);
        }

        // Manejar diferentes tipos de contenido
        const contentType = response.headers.get('content-type') || '';

        if (contentType.includes('application/pdf') || contentType.includes('application/octet-stream')) {
            return await response.blob();
        }

        // Si no es PDF, intentar parsear como JSON
        try {
            return await response.json();
        } catch {
            return await response.text();
        }

    } catch (error) {
        // console.error("Error fetching report:", error);
        //alert(`Error fetching report: ${error.message}`);
        throw error;
    }
};

export const fetchGetReport_ok_pdfcsv = async (endpoint, params, responseType = 'auto') => {
    try {
        if (!endpoint || !params) {
            throw new Error("Endpoint o parámetros no válidos.");
        }

        const encodedParams = params.split('/').map(encodeURIComponent).join('/');
        const url = `${appVar.gApiUrl}/${endpoint}/${encodedParams}`;

        const response = await apiFetch(url, {
            method: 'GET',
            headers: {
                'Accept': responseType === 'csv' ? 'text/csv' : 'application/pdf'
            }
        });

        if (!response.ok) {
            const errorData = await response.text();
            throw new Error(`Error ${response.status}: ${errorData}`);
        }

        const contentType = response.headers.get('content-type') || '';

        // Manejar CSV explícitamente si se solicita
        if (responseType === 'csv' || contentType.includes('text/csv')) {
            return await response.text();
        }

        // Manejar PDF
        if (contentType.includes('application/pdf') || contentType.includes('application/octet-stream')) {
            return await response.blob();
        }

        // Manejar otros tipos (JSON o texto)
        try {
            return await response.json();
        } catch {
            return await response.text();
        }

    } catch (error) {
        throw error;
    }
};

export const fetchGetReport = async (endpoint, params, responseType = 'auto') => {
    try {
        if (!endpoint || !params) {
            throw new Error("Endpoint o parámetros no válidos.");
        }


        const encodedParams = params.split('/').map(encodeURIComponent).join('/');
        const url = `${appVar.gApiUrl}/${endpoint}/${encodedParams}`;

        // Configurar Accept header según el tipo de respuesta solicitado.
        // Siempre se agrega application/json + text/plain para que, si el backend
        // responde un error con cuerpo JSON (p.ej. 404 {codigo, mensaje}), la
        // negociación de contenido de Web API no descarte ese cuerpo por no
        // coincidir con el Accept "binario".
        let acceptHeader;
        switch (responseType) {
            case 'csv':
                acceptHeader = 'text/csv';
                break;
            case 'xls':
                acceptHeader = 'application/vnd.ms-excel';
                break;
            case 'xlsx':
                acceptHeader = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                break;
            case 'pdf':
                acceptHeader = 'application/pdf';
                break;
            default:
                acceptHeader = 'application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv';
        }
        acceptHeader += ', application/json, text/plain';

        const response = await apiFetch(url, {
            method: 'GET',
            headers: {
                'Accept': acceptHeader
            }
        });

        const contentType = response.headers.get('content-type') || '';

        if (!response.ok) {
            const errorText = await response.text();
            let errorPayload = null;
            try {
                errorPayload = JSON.parse(errorText);
            } catch {
                // Cuerpo no JSON: se conserva el texto plano.
            }

            const errorMessage =
                errorPayload?.mensaje ||
                errorPayload?.message ||
                errorText ||
                `Error ${response.status}`;
            const error = new Error(errorMessage);
            error.status = response.status;
            if (errorPayload) error.response = errorPayload;
            throw error;
        }

        // Manejar tipos específicos
        switch (true) {
            case responseType === 'csv' || contentType.includes('text/csv'):
                return await response.text();

            case responseType === 'xls' || contentType.includes('application/vnd.ms-excel'):
            case responseType === 'xlsx' || contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'):
                return await response.blob();

            case contentType.includes('application/pdf') || contentType.includes('application/octet-stream'):
                return await response.blob();

            case contentType.includes('application/json'): {
                // 200 pero con JSON: el reporte no devolvió archivo (sin datos / error lógico).
                const data = await response.json();
                const msg = data?.mensaje || data?.message;
                if (msg) {
                    const error = new Error(msg);
                    error.status = response.status;
                    error.response = data;
                    throw error;
                }
                return data;
            }

            default:
                // Manejar otros tipos (texto)
                return await response.text();
        }

    } catch (error) {
        logger.error('API GET (report) falló', { endpoint, params, detalle: error.message || String(error) });
        throw error;
    }
};

// POST con cuerpo JSON que devuelve un archivo binario (zip/pdf/...).
// Si el servidor responde JSON (p.ej. error o "sin resultados") se devuelve ese objeto.
export const fetchPostReport = async (endpoint, body) => {
    try {
        if (!endpoint) {
            throw new Error("Endpoint no válido.");
        }


        const response = await apiFetch(`${appVar.gApiUrl}/${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/zip, application/octet-stream, application/pdf, application/json'
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const errorText = await response.text();
            let errorPayload = null;
            try {
                errorPayload = JSON.parse(errorText);
            } catch {
                // texto plano
            }
            const errorMessage = errorPayload?.mensaje || errorPayload?.message || errorText || `HTTP error ${response.status}`;
            const error = new Error(`HTTP error! Status: ${response.status}, Message: ${errorMessage}`);
            if (errorPayload) {
                error.response = errorPayload;
            }
            throw error;
        }

        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            return await response.json();
        }
        return await response.blob();

    } catch (error) {
        logger.error(`API POST ${endpoint} (report) falló`, { endpoint, detalle: error.message || String(error) });
        throw error;
    }
};

